/**
 * GiftRecipientsField — who the additional passes go to.
 *
 * One block per additional pass: the buyer keeps pass #1, so a quantity of N
 * renders N − 1 recipients. Values live in the purchase context; this file only
 * renders them and reports edits.
 *
 * ELIGIBILITY is advisory and deliberately vague, and it is NOT lifted to the
 * parent: the answer is server state, it stays in react-query, and Pay never
 * gates on it. A buyer who ignores the red line is refused by the purchase
 * itself — before any charge — and that refusal lands back here (`refusedIndex`).
 * The check runs against the
 * COMMITTED address (on blur), never the live input, and the server answers one
 * bit with no reason — so the copy is one line that fits every reason and cannot
 * be used to learn anything specific about the person named.
 *
 * @module pages/subscription/steps/GiftRecipientsField
 */

import { useState } from 'react';
import { InfoIcon } from '@butter/ui/icons';
import { useGiftEligibility, type GiftRecipientDraft } from '~/services/internalApi/subscription';
import { GIFT_RECIPIENT_INELIGIBLE_COPY } from './giftRecipients';

const inputClass =
  'w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-transparent ' +
  'focus:ring-2 focus:ring-theme-500 disabled:bg-gray-50 data-[invalid=true]:border-red-400';

function RecipientBlock({
  index,
  passNumber,
  draft,
  clubId,
  canCheck,
  disabled,
  serverRefused,
  onChange,
}: {
  index: number;
  /** Which pass in the order this recipient holds — 1-based, for the legend only. */
  passNumber: number;
  draft: GiftRecipientDraft;
  clubId: number | undefined;
  canCheck: boolean;
  disabled: boolean;
  /** The purchase itself refused this recipient (the saga's answer, not the pre-check's). */
  serverRefused: boolean;
  onChange: (index: number, patch: Partial<GiftRecipientDraft>) => void;
}) {
  // Captured input: the address as of the last blur. Written only from the handler.
  const [committedEmail, setCommittedEmail] = useState('');
  const { eligible } = useGiftEligibility({ clubId, email: committedEmail, enabled: canCheck });

  // The answer is about the COMMITTED address. While the buyer is retyping, a
  // stale "no" about the old address must not sit under the new one.
  const answerIsCurrent = committedEmail.trim().toLowerCase() === draft.email.trim().toLowerCase();
  const refused = (answerIsCurrent && eligible === false) || serverRefused;

  const id = `gift-recipient-${index}`;
  return (
    <fieldset className="flex flex-col gap-4" data-testid={`gift-recipient-${index}`} disabled={disabled}>
      <legend className="sr-only">Recipient for pass {passNumber}</legend>
      <div className="text-sm">
        <label htmlFor={`${id}-name`} className="mb-2 block font-medium text-fg-primary">
          Recipient Name
        </label>
        <input
          id={`${id}-name`}
          type="text"
          autoComplete="off"
          value={draft.name}
          onChange={(e) => onChange(index, { name: e.target.value })}
          placeholder="Bob Smith"
          data-testid={`gift-recipient-${index}-name`}
          className={inputClass}
        />
      </div>
      <div className="text-sm">
        <label htmlFor={`${id}-email`} className="mb-2 block font-medium text-fg-primary">
          Email
        </label>
        <input
          id={`${id}-email`}
          type="email"
          autoComplete="off"
          value={draft.email}
          onChange={(e) => onChange(index, { email: e.target.value })}
          onBlur={(e) => setCommittedEmail(e.target.value)}
          placeholder="youremail@mail.com"
          data-invalid={refused}
          aria-invalid={refused}
          aria-describedby={refused ? `${id}-refused` : undefined}
          data-testid={`gift-recipient-${index}-email`}
          className={inputClass}
        />
        {refused && (
          <p id={`${id}-refused`} role="alert" className="mt-2 text-sm text-red-600" data-testid={`gift-recipient-${index}-refused`}>
            {GIFT_RECIPIENT_INELIGIBLE_COPY}
          </p>
        )}
      </div>
    </fieldset>
  );
}

export function GiftRecipientsField({
  count,
  recipients,
  clubId,
  canCheck,
  disabled = false,
  refusedIndex = null,
  giftAll = false,
  message,
  onChange,
}: {
  /** How many recipients to collect — see `recipientsNeeded`. Renders nothing at 0. */
  count: number;
  recipients: GiftRecipientDraft[];
  clubId: number | undefined;
  /** Only an authenticated buyer can run the pre-check. */
  canCheck: boolean;
  disabled?: boolean;
  /** Which recipient the PURCHASE refused, from the API's `param`. */
  refusedIndex?: number | null;
  /**
   * The buyer keeps none, so `recipients[0]` is pass #1 rather than pass #2
   * Shifts the labelling only — the fields are identical.
   */
  giftAll?: boolean;
  /** The club's gifting message; the system copy when the promo sets none. */
  message: string;
  onChange: (index: number, patch: Partial<GiftRecipientDraft>) => void;
}) {
  if (count < 1) return null;

  return (
    <section className="flex flex-col gap-5 rounded-3xl border border-gray-200 p-6" data-testid="gift-recipients">
      <p className="text-xs text-fg-tertiary">
        {giftAll
          ? (count === 1 ? 'Gift This Pass' : 'Gift These Passes')
          : (count === 1 ? 'Gift Additional Pass' : 'Gift Additional Passes')}
      </p>
      <div>
        <p className="flex items-center gap-1.5 text-sm font-medium text-blue-600">
          <InfoIcon ariaHidden className="size-4 shrink-0" />
          Before you continue
        </p>
        <p className="mt-2 text-sm text-fg-primary" data-testid="gifting-message">
          {message}
        </p>
      </div>
      {Array.from({ length: count }, (_, index) => (
        <RecipientBlock
          key={index}
          index={index}
          passNumber={giftAll ? index + 1 : index + 2}
          draft={recipients[index] ?? EMPTY_DRAFT}
          clubId={clubId}
          canCheck={canCheck}
          disabled={disabled}
          serverRefused={refusedIndex === index}
          onChange={onChange}
        />
      ))}
    </section>
  );
}

/** Module scope — a stable stand-in for a recipient not typed yet. */
const EMPTY_DRAFT: GiftRecipientDraft = { name: '', email: '' };
